//
// # SRV arrow overlay
//
// Self-contained module owning the entire SRV canvas overlay code path:
// rendering the per-violation arrows, hit-testing arrow clicks, and the
// gating that decides whether any of that runs.
//
// Design note (deviation from the original plan): the plan had this module read
// `workspace.srv.getState()` directly. EditorCore has no `workspace` reference
// (the IDE → Editor dependency is one-way), so instead the IDE *pushes* a small
// `SrvOverlay` payload into the schematic content (mirroring how DRC/LVS push
// highlights via `editor.highlightDrcError`). The IDE only pushes a non-null
// overlay when SRV is active (results present, panel visible, highlights
// enabled), so the gate here collapses to a single null check — there is no
// `panelVisible` / `highlightsEnabled` logic to duplicate in EditorCore.
//
// Because EditorCore cannot reach the model, the overlay carries an
// `onArrowClick` callback (closing over `workspace.srv.selectViolation`) so an
// arrow click can route back to the panel selection.
//
// Anchors are *grouped*: several violations can report the same world point (a
// wire end that is both dangling and off-net), and drawing one arrow per
// violation stacked them invisibly on top of each other with only the
// tie-breaking one reachable by a click. Instead each distinct point draws a
// single arrow, annotated with a "xN" count when it stands for more than one
// violation, and clicking it cycles through that point's violations.
//

import { Point, graphics, model, srv } from "GenAlphaModel";
import * as style from "../../style";
import { DrawTo } from "../../draw";

// The payload the IDE pushes into the schematic content. Non-null only while
// SRV is active; a null overlay means "draw nothing, hit-test nothing".
export interface SrvOverlay {
  run: srv.SrvRunResult;
  highlightedViolationIndex: number | null;
  // Routes an arrow click back to the SrvModel (panel row selection).
  onArrowClick: (violationIndex: number) => void;
}

// Fixed on-screen sizes (pixels), divided by the world→screen scale at draw
// time so the arrows stay legible at any zoom (matching the badge mental model).
const ARROWHEAD_PX = 13;
const SHAFT_PX = 9;
const SHAFT_WIDTH_PX = 2;
const HIGHLIGHT_FACTOR = 1.6;

// Hit-target geometry. The capsule deliberately does NOT reach the arrow's tip:
// the tip sits exactly on the anchor — a wire endpoint or a pin — which is the
// thing the user must still be able to click and drag to *fix* the violation.
// The capsule therefore starts `HIT_CLEARANCE_PX` up the arrowhead, leaving a
// clear disc of (clearance - radius) ≈ 5px around the anchor itself, smaller
// than the wire endpoint hover ring (8px) so the endpoint stays grabbable.
// Both scale with HIGHLIGHT_FACTOR alongside the drawn arrow.
const HIT_RADIUS_PX = 6;
const HIT_CLEARANCE_PX = 11;

// The "xN" count annotation drawn beside an arrow that stands for several
// violations at the same point. Placed clear of the shaft end and excluded from
// the hit capsule, so the clickable region stays exactly the arrow body.
const COUNT_GAP_PX = 5;
const COUNT_SIZE_PX = 11;

// Anchors within this distance of each other (world units) are considered the
// same point for grouping. The anchors come from the rules engine as floats;
// co-located ones are expected to be bit-identical, but quantizing costs
// nothing and avoids a stray last-bit difference splitting a group.
const KEY_QUANTUM = 1e-6;

// Arrow colors follow the active theme. The selected arrow uses the inverse of
// the canvas background (white on a dark theme, black on a light theme) so the
// focused violation stands out at a glance against the background it sits on (it
// is also enlarged by HIGHLIGHT_FACTOR). Unselected arrows use the theme's grid
// color so they read as a quiet, theme-consistent marker. Both are resolved at
// draw time since the global theme can change after this module loads.
function selectedArrowColor(): style.Color {
  return style.Theme.isDark(style.Theme.get())
    ? style.Color.white
    : style.Color.black;
}

function unselectedArrowColor(): style.Color {
  return style.Theme.isDark(style.Theme.get())
    ? style.Color.gridLine.darkBold
    : style.Color.gridLine.lightBold;
}

// The hovered arrow is drawn in the same orange as the wire-endpoint hover ring
// (see `endpointHoverStyle` in schematic.ts), so "the thing under the cursor is
// what the next click will act on" reads the same everywhere on the canvas.
const HOVER_COLOR: style.Color = style.Color.new(255, 140, 0, 1.0);

// How an arrow is drawn. Selection comes from the model (the panel's
// highlighted row) and is the only state that changes the arrow's *size*;
// hover comes from the cursor and changes color only. Scaling on hover would
// feed the cursor's position back into the hit geometry that decides whether
// the cursor is hovering, which oscillates at the boundary.
type ArrowState = "normal" | "hovered" | "selected";

// Tail direction (unit vector): the arrow points at its anchor with the shaft
// trailing toward the up-right, a fixed direction so it never rotates with the
// schematic and never overlays the anchor it points at.
const DIR = { x: Math.SQRT1_2, y: Math.SQRT1_2 };
const PERP = { x: -Math.SQRT1_2, y: Math.SQRT1_2 };

// One distinct anchor point, with every violation reporting it. `indices` is in
// ascending violation order (the panel's own order), so click-to-cycle walks
// the point's violations top-down through the Results list.
interface ArrowGroup {
  key: string;
  point: Point;
  indices: Array<number>;
}

// Per-arrow hit target, in world coordinates, rebuilt on every gate-passing
// draw. Module-private so the rendering core stays ignorant of it. The target
// is a capsule — a segment along the arrow's axis thickened by `radius` — that
// covers the arrow body but stops short of the tip; see HIT_CLEARANCE_PX.
interface HitTarget {
  key: string;
  head: Point; // capsule start, up the arrowhead from the anchor
  tail: Point; // capsule end, at the shaft end
  radius: number;
  indices: Array<number>;
}

// NOTE: module-level, not per-Schematic — with two schematic editors open the
// most recently drawn one owns the cache. Pre-existing, and harmless today
// because only the visible editor draws, but the state belongs on the content
// object if this ever needs to be correct for multiple live canvases.
let hitTargets: Array<HitTarget> = [];

// The group key of the arrow currently under the cursor, or null. Drives the
// hover color and lets the schematic suppress its own endpoint hover marker.
let hoveredKey: string | null = null;

// Invalidate the cached hit targets. Called on a gated-off draw so stale arrows
// can't be clicked; also the natural eviction hook if arrow bitmap caching is
// added later.
export function clearSrvArrowHitRects(): void {
  hitTargets = [];
  hoveredKey = null;
}

// Draw one arrow per distinct anchor point. Early-returns (clearing the hit
// cache) when the overlay is gated off.
export function drawSrvArrows(to: DrawTo, overlay: SrvOverlay | null): unknown {
  if (overlay === null) {
    clearSrvArrowHitRects();
    return undefined;
  }

  const scale = to.scale;
  const rebuilt: Array<HitTarget> = [];
  const groups = groupAnchors(overlay.run.violations);

  // A hover on an arrow that no longer exists (results re-run, violation fixed)
  // must not keep suppressing the endpoint marker. The next mouse move would
  // clear it anyway, but the user may not have moved.
  if (hoveredKey !== null && !groups.some((g) => g.key === hoveredKey)) {
    hoveredKey = null;
  }

  for (const group of groups) {
    const selected =
      overlay.highlightedViolationIndex !== null &&
      group.indices.includes(overlay.highlightedViolationIndex);
    const state: ArrowState = selected
      ? "selected"
      : group.key === hoveredKey
      ? "hovered"
      : "normal";
    const factor = selected ? HIGHLIGHT_FACTOR : 1;

    drawArrow(to, group.point, scale, state, group.indices.length);

    // The capsule spans the arrow body: from `HIT_CLEARANCE_PX` along the axis
    // (clear of the anchor) out to the shaft end.
    rebuilt.push({
      key: group.key,
      head: add(
        group.point,
        scaleVec(DIR, (HIT_CLEARANCE_PX * factor) / scale)
      ),
      tail: add(
        group.point,
        scaleVec(DIR, ((ARROWHEAD_PX + SHAFT_PX) * factor) / scale)
      ),
      radius: (HIT_RADIUS_PX * factor) / scale,
      indices: group.indices,
    });
  }

  hitTargets = rebuilt;
  return undefined;
}

// Return the violation indices of the arrow whose capsule contains
// `worldPoint` (the mouse position in schematic coordinates), or null. The
// closest arrow (by distance to its capsule axis) wins when targets overlap.
// Gated identically to drawSrvArrows: when the overlay is off the cache is
// empty, so this returns null.
export function hitTestSrvArrow(worldPoint: Point): Array<number> | null {
  const target = nearestTarget(worldPoint);
  return target === null ? null : target.indices;
}

// Pick the violation a click on this arrow should select: the one after
// `current` in the arrow's list, wrapping, or the first when `current` is null
// or belongs to a different arrow. The current highlight *is* the cycle cursor,
// so no state is kept and a re-run can't leave a stale one behind.
export function nextViolationIndex(
  indices: ReadonlyArray<number>,
  current: number | null
): number | null {
  if (indices.length === 0) return null;
  const at = current === null ? -1 : indices.indexOf(current);
  return indices[(at + 1) % indices.length] ?? null;
}

// Update the hovered arrow from the cursor position. Returns whether an arrow
// is hovered (the caller suppresses its own hover markers when so) and whether
// that changed (the caller redraws only on a transition, so plain cursor motion
// stays cheap).
export function updateSrvArrowHover(worldPoint: Point): {
  hovered: boolean;
  changed: boolean;
} {
  const target = nearestTarget(worldPoint);
  const key = target === null ? null : target.key;
  const changed = key !== hoveredKey;
  hoveredKey = key;
  return { hovered: key !== null, changed };
}

// --- internals -------------------------------------------------------------

// Collapse every (violation, anchor point) pair into one entry per distinct
// point, preserving first-seen point order and ascending violation order
// within each group.
function groupAnchors(
  violations: ReadonlyArray<srv.SrvViolation>
): Array<ArrowGroup> {
  const groups = new Map<string, ArrowGroup>();
  violations.forEach((violation, violationIndex) => {
    for (const point of violation.points) {
      const key = pointKey(point);
      const existing = groups.get(key);
      if (existing === undefined) {
        groups.set(key, { key, point, indices: [violationIndex] });
      } else if (!existing.indices.includes(violationIndex)) {
        // A single violation can list the same point twice; count it once.
        existing.indices.push(violationIndex);
      }
    }
  });
  return Array.from(groups.values());
}

function pointKey(point: Point): string {
  const qx = Math.round(point.x / KEY_QUANTUM);
  const qy = Math.round(point.y / KEY_QUANTUM);
  return `${String(qx)},${String(qy)}`;
}

// The hit target whose capsule contains `worldPoint` and whose axis is nearest,
// or null when the point is outside every capsule.
function nearestTarget(worldPoint: Point): HitTarget | null {
  let best: { target: HitTarget; dist2: number } | null = null;
  for (const target of hitTargets) {
    const dist2 = distToSegment2(worldPoint, target.head, target.tail);
    if (dist2 <= target.radius * target.radius) {
      if (best === null || dist2 < best.dist2) {
        best = { target, dist2 };
      }
    }
  }
  return best === null ? null : best.target;
}

function arrowColor(state: ArrowState): style.Color {
  switch (state) {
    case "selected":
      return selectedArrowColor();
    case "hovered":
      return HOVER_COLOR;
    case "normal":
      return unselectedArrowColor();
  }
}

function drawArrow(
  to: DrawTo,
  tip: Point,
  scale: number,
  state: ArrowState,
  violationCount: number
): void {
  const factor = state === "selected" ? HIGHLIGHT_FACTOR : 1;
  const color = arrowColor(state);
  const headLen = (ARROWHEAD_PX * factor) / scale;
  const shaftLen = (SHAFT_PX * factor) / scale;
  const halfBase = 0.42 * headLen;

  const baseCenter = add(tip, scaleVec(DIR, headLen));
  const corner1 = add(baseCenter, scaleVec(PERP, halfBase));
  const corner2 = add(baseCenter, scaleVec(PERP, -halfBase));
  const shaftEnd = add(tip, scaleVec(DIR, headLen + shaftLen));

  // Shaft first, arrowhead (filled) on top.
  to.path(graphics.Path.fromPoints([baseCenter, shaftEnd]), {
    cap: "round",
    join: "round",
    linewidth: (SHAFT_WIDTH_PX * factor) / scale,
    stroke: color,
    fill: style.Special.None,
  });
  to.polygon(
    graphics.Polygon.fromTuples(
      [tip.x, tip.y],
      [corner1.x, corner1.y],
      [corner2.x, corner2.y]
    ),
    {
      cap: "round",
      join: "round",
      linewidth: 0,
      fill: color,
      stroke: style.Special.None,
    }
  );

  if (violationCount > 1) {
    drawCount(to, shaftEnd, scale, factor, color, violationCount);
  }
}

// The "xN" annotation, to the right of the shaft end so it reads as a suffix to
// the arrow and never covers the anchor. Sized in screen pixels like the arrow.
function drawCount(
  to: DrawTo,
  shaftEnd: Point,
  scale: number,
  factor: number,
  color: style.Color,
  violationCount: number
): void {
  const size = (COUNT_SIZE_PX * factor) / scale;
  const loc = {
    x: shaftEnd.x + (COUNT_GAP_PX * factor) / scale,
    y: shaftEnd.y,
  };
  const textStyle: style.TextStyle = {
    family: style.THE_FONT,
    style: "normal",
    weight: 700,
    size,
    fill: color,
    stroke: style.Special.None,
  };
  to.text(
    graphics.Text.fromPartial({
      text: `x${String(violationCount)}`,
      loc,
      height: size,
      alignment: graphics.Alignment.Left,
      baseline: graphics.Baseline.Middle,
      rotation: 0,
      hidden: false,
    }),
    textStyle,
    { xform: model.Xform.default() }
  );
}

function add(a: Point, b: Point): Point {
  return { x: a.x + b.x, y: a.y + b.y };
}

function scaleVec(v: Point, k: number): Point {
  return { x: v.x * k, y: v.y * k };
}

// Squared distance from point `p` to the line segment [a, b], clamping the
// projection to the segment so the endpoints round off into the capsule caps.
function distToSegment2(p: Point, a: Point, b: Point): number {
  const abx = b.x - a.x;
  const aby = b.y - a.y;
  const lenSq = abx * abx + aby * aby;
  const t =
    lenSq === 0
      ? 0
      : Math.max(
          0,
          Math.min(1, ((p.x - a.x) * abx + (p.y - a.y) * aby) / lenSq)
        );
  const dx = p.x - (a.x + t * abx);
  const dy = p.y - (a.y + t * aby);
  return dx * dx + dy * dy;
}
